// LoginPage.js

import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';
import axios from 'axios';
import Header from './Header';

const RETRY_DELAYS = [3000, 7000, 13000];

const LoginPage = ({ login }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempt, setAttempt] = useState(0); // 0 = primeira tentativa

  const timeoutsRef = useRef([]);

  const clearScheduledRetries = () => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  };

  const buildFriendlyError = (status, rawMessage) => {
    if (status === 503) return 'Serviço indisponível no momento (503). Vou tentar novamente automaticamente.';
    if (status === 504) return 'Timeout ao acessar o servidor (504). Vou tentar novamente.';
    if (status === 401 || status === 403) return 'Acesso não autorizado. Verifique permissões/chave de acesso.';
    if (status) return `Erro ao acessar o servidor (${status}). Vou tentar novamente.`;
    return rawMessage || 'Não foi possível conectar ao servidor. Vou tentar novamente.';
  };

  const doLoginRequest = async () => {
    const requestBody = new FormData();
    requestBody.append('login', username);
    requestBody.append('password', password);

    const url = `${process.env.REACT_APP_BASE_URL}/api/Login`;

    // Se seu backend precisar de headers específicos, ajuste aqui.
    const response = await fetch(url, {
      method: 'POST',
      body: requestBody,
    });

    // Se status não ok, joga erro com status
    if (!response.ok) {
      const err = new Error('HTTP_ERROR');
      err.status = response.status;
      throw err;
    }

    return response.json();
  };

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    // evita retry com campos vazios
    if (!username || !password) {
      setLoginError('Preencha usuário e senha.');
      return;
    }

    clearScheduledRetries();
    setLoginError('');
    setIsLoading(true);
    setAttempt(0);

    const tryOnce = async (tryIndex) => {
      try {
        setAttempt(tryIndex);

        const data = await doLoginRequest();

        if (data?.Login) {
          localStorage.setItem('user', JSON.stringify(data));
          window.location.href = 'controlpanel';
          return; // sucesso
        }

        // credenciais inválidas: não faz retry (não adianta)
        setLoginError('Usuário ou senha inválidos');
        localStorage.clear();
        setIsLoading(false);
      } catch (error) {
        const status = error?.status;

        // Se for erro de rede (fetch falhou) o status vem undefined
        const friendly = buildFriendlyError(status, error?.message);

        // Se já tentou todas as vezes, para
        if (tryIndex >= RETRY_DELAYS.length) {
          setLoginError(
            status
              ? `${friendly} (Tente novamente mais tarde.)`
              : `${friendly} (Verifique sua internet ou o endereço do servidor.)`
          );
          setIsLoading(false);
          return;
        }

        // agenda próxima tentativa
        const nextDelay = RETRY_DELAYS[tryIndex];
        setLoginError(`${friendly} Nova tentativa em ${Math.round(nextDelay / 1000)}s...`);

        const t = setTimeout(() => {
          tryOnce(tryIndex + 1);
        }, nextDelay);

        timeoutsRef.current.push(t);
      }
    };

    // primeira tentativa imediata (tryIndex 0)
    tryOnce(0);
  };

  const handleKeyPress = async (e) => {
    if (e.key === 'Enter') {
      handleLogin(e);
    }
  };

  useEffect(() => {
    // limpa somente ao montar
    localStorage.clear();

    // cleanup: se sair da página, cancela retries pendentes
    return () => clearScheduledRetries();
  }, []);

  return (
    <main className="container-fluid loginBackground" style={{ position: 'relative' }}>
      {/* Overlay de loading */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="card rounded-4 shadow-lg p-4" style={{ minWidth: 320 }}>
            <div className="d-flex align-items-center gap-3">
              <div className="spinner-border" role="status" aria-hidden="true" />
              <div>
                <div style={{ fontWeight: 600 }}>Aguarde...</div>
                <div style={{ fontSize: 14, opacity: 0.8 }}>
                  Aguarde, fazendo o login...
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="section register min-vh-100 d-flex flex-column align-items-center justify-content-center py-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-4 col-md-6 d-flex flex-column align-items-center justify-content-center">

              <div className="d-flex justify-content-center py-4">
                <div className="second">
                  <p>
                    <span className="logoverde">AI</span>
                    <span className="logobranco">DATACAPTURE</span>
                    <span className="logoverde">7</span>
                  </p>
                </div>
              </div>

              <div className="card rounded-4 mb-3 shadow-lg">
                <div className="card-body">

                  <div className="pt-4 pb-2">
                    <h5 className="card-title text-center pb-0 fs-4 loginTitle">Fazer Login</h5>
                    <p className="text-center small">Entre com seu usuário e senha para fazer login.</p>
                  </div>

                  <div className="row g-3" noValidate>
                    <div className="col-12">
                      <label className="form-label">Usuário</label>
                      <div className="input-group has-validation">
                        <input
                          type="text"
                          className="form-control"
                          id="Username"
                          onChange={(e) => setUsername(e.target.value)}
                          onKeyDown={handleKeyPress}
                          disabled={isLoading}
                          required
                        />
                        <div className="invalid-feedback">Por favor, preencha o campo Usuário.</div>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Senha</label>
                      <input
                        type="password"
                        className="form-control"
                        id="Password"
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyPress}
                        disabled={isLoading}
                        required
                      />
                      <div className="invalid-feedback">Por favor, preencha o campo senha.</div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" name="remember" value="true" id="rememberMe" disabled={isLoading} />
                        <label className="form-check-label">Lembrar senha</label>
                      </div>
                    </div>

                    <div className="col-12">
                      {loginError && (
                        <div className="alert alert-danger text-center mb-0">
                          <p className="mb-0">{loginError}</p>
                        </div>
                      )}
                    </div>

                    <div className="col-12">
                      <button className="btn w-100 loginButton" onClick={handleLogin} disabled={isLoading}>
                        {isLoading ? 'Conectando...' : 'Entrar'}
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              {/* Se quiser, pode colocar um pequeno texto de dica abaixo */}
              {/* <div className="text-center small" style={{ opacity: 0.8 }}>
                Se aparecer 503, normalmente é a Function App parada ou reiniciando.
              </div> */}

            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
